/**
 * What outside sources say of a domain (ADR-0050): its history in the Web
 * Archive, its authority in the link graph, its backlinks and their spam. Read
 * weekly for every site and on request for a domain one is thinking of buying.
 */

export const METRIC_SOURCES = ['wayback', 'openpagerank', 'dataforseo', 'ahrefs'] as const;
export type MetricSource = (typeof METRIC_SOURCES)[number];

/** How long a read is good for before the weekly pass takes it again. */
export const METRICS_MAX_AGE_MS = 7 * 24 * 60 * 60_000;

export interface WaybackFacts {
  readonly firstCapture: string | null;
  readonly lastCapture: string | null;
  /** The years with at least one capture, ascending. */
  readonly years: readonly number[];
}

export interface OpenPageRankFacts {
  readonly found: boolean;
  /** 0–10, from the Common Crawl link graph. */
  readonly pageRank: number | null;
  /** Place among all domains; lower is stronger. */
  readonly rank: number | null;
  readonly referringDomains: number | null;
}

export interface DataForSeoFacts {
  /** DataForSEO's own authority, 0–1000. */
  readonly rank: number | null;
  readonly backlinks: number | null;
  readonly referringDomains: number | null;
  readonly referringMainDomains: number | null;
  readonly referringIps: number | null;
  readonly brokenBacklinks: number | null;
  /** How spammy the domain itself looks, 0–100. */
  readonly spamScore: number | null;
  /** How spammy the links to it look, 0–100. */
  readonly backlinksSpamScore: number | null;
  readonly firstSeen: string | null;
}

export interface AhrefsFacts {
  /** Ahrefs Domain Rating, 0–100: free with any Ahrefs key. */
  readonly domainRating: number | null;
  /** The latest month of the history, when a paid key took it. */
  readonly referringDomains: number | null;
  readonly organicTraffic: number | null;
  /** When the monthly history was last taken; null while it never was. */
  readonly historyAt: string | null;
  /** Why the last try at the history failed: a free key, a plan without Site Explorer. */
  readonly historyError: string | null;
}

export interface SourceRead<T> {
  readonly data: T | null;
  readonly fetchedAt: string | null;
  readonly error: string | null;
}

export interface DomainMetricsView {
  readonly domain: string;
  readonly wayback: SourceRead<WaybackFacts> | null;
  readonly openpagerank: SourceRead<OpenPageRankFacts> | null;
  readonly dataforseo: SourceRead<DataForSeoFacts> | null;
  readonly ahrefs: SourceRead<AhrefsFacts> | null;
  /** A read was asked for and the worker has not taken it yet. */
  readonly pending: boolean;
  readonly notes: readonly DomainNote[];
  /** Null when no source has anything to go on. */
  readonly verdict: 'clean' | 'care' | 'risk' | null;
}

export interface DomainNote {
  readonly severity: 'risk' | 'care' | 'info';
  readonly code: 'no_history' | 'history_gap' | 'spam_high' | 'spam_raised' | 'links_concentrated' | 'links_broken' | 'not_in_graph' | 'young';
  readonly params: Readonly<Record<string, string | number>>;
}

/**
 * What a person should look at before buying or building on a domain. Hints,
 * not a judgement: a gap in the archive is often a change of owner, and a
 * high spam score is often a neighbour's doing — both worth a look.
 */
export function domainNotes(
  metrics: { readonly wayback: WaybackFacts | null; readonly openpagerank: OpenPageRankFacts | null; readonly dataforseo: DataForSeoFacts | null },
  now: Date,
): DomainNote[] {
  const notes: DomainNote[] = [];
  const { wayback, openpagerank, dataforseo } = metrics;

  if (wayback) {
    if (wayback.years.length === 0) {
      notes.push({ severity: 'info', code: 'no_history', params: {} });
    } else {
      for (let index = 1; index < wayback.years.length; index += 1) {
        const from = wayback.years[index - 1]!;
        const to = wayback.years[index]!;
        if (to - from >= 3) notes.push({ severity: 'care', code: 'history_gap', params: { from, to } });
      }
      const first = wayback.years[0]!;
      if (now.getUTCFullYear() - first < 1) notes.push({ severity: 'info', code: 'young', params: { year: first } });
    }
  }

  if (dataforseo) {
    const spam = dataforseo.spamScore ?? dataforseo.backlinksSpamScore;
    if (spam !== null && spam >= 60) notes.push({ severity: 'risk', code: 'spam_high', params: { score: spam } });
    else if (spam !== null && spam >= 30) notes.push({ severity: 'care', code: 'spam_raised', params: { score: spam } });
    const { backlinks, referringDomains, brokenBacklinks } = dataforseo;
    if (backlinks !== null && referringDomains !== null && referringDomains > 0 && backlinks >= 1_000 && backlinks / referringDomains >= 500) {
      notes.push({ severity: 'care', code: 'links_concentrated', params: { backlinks, referringDomains } });
    }
    if (backlinks !== null && brokenBacklinks !== null && backlinks >= 20 && brokenBacklinks / backlinks >= 0.5) {
      notes.push({ severity: 'info', code: 'links_broken', params: { share: Math.round((brokenBacklinks / backlinks) * 100) } });
    }
  }

  if (openpagerank && !openpagerank.found) notes.push({ severity: 'info', code: 'not_in_graph', params: {} });

  return notes;
}

export function domainVerdict(notes: readonly DomainNote[], anyData: boolean): DomainMetricsView['verdict'] {
  if (!anyData) return null;
  if (notes.some((note) => note.severity === 'risk')) return 'risk';
  if (notes.some((note) => note.severity === 'care')) return 'care';
  return 'clean';
}

/** The name the sources are asked about: the host without its www. */
export function metricsDomain(host: string): string {
  return host.toLowerCase().replace(/\.$/, '').replace(/^www\./, '');
}

/** Candidate names for a check, as typed: one per line or comma, bare hosts, no duplicates. */
export function parseDomainList(text: string, limit = 20): { domains: string[]; invalid: string[] } {
  const seen = new Set<string>();
  const invalid: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const value = raw.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    if (!value) continue;
    if (!/^(?=.{3,253}$)([a-z0-9-]{1,63}\.)+[a-z0-9-]{2,63}$/.test(value)) {
      invalid.push(raw.trim());
      continue;
    }
    seen.add(metricsDomain(value));
  }
  return { domains: [...seen].slice(0, limit), invalid };
}

/**
 * The timeline (ADR-0054): each figure a source gives, one point a day.
 * Search Console's are the site's own; the rest are the domain's as the link
 * graph sees it.
 */
export const TIMELINE_METRICS = ['clicks', 'impressions', 'position', 'domain_rating', 'referring_domains', 'organic_traffic', 'page_rank', 'backlinks'] as const;
export type TimelineMetric = (typeof TIMELINE_METRICS)[number];
export type TimelineSource = MetricSource | 'google';

export interface TimelinePoint {
  /** YYYY-MM-DD. */
  readonly date: string;
  readonly value: number;
}

export interface TimelineSeries {
  readonly metric: TimelineMetric;
  readonly source: TimelineSource;
  readonly points: readonly TimelinePoint[];
}

/** A site's timeline: every series there is for its domain, oldest point first. */
export interface SiteTimelineView {
  readonly domain: string;
  readonly series: readonly TimelineSeries[];
  /** What could add to it: a Google key, an Ahrefs key and whether it takes history. */
  readonly google: boolean;
  readonly ahrefs: 'off' | 'current' | 'history';
}

/** The points a source's read adds to the timeline, on the day it was read. */
export function pointsOf(source: MetricSource, data: unknown): { metric: TimelineMetric; value: number }[] {
  if (!data || typeof data !== 'object') return [];
  const facts = data as Record<string, unknown>;
  const points: { metric: TimelineMetric; value: number }[] = [];
  const add = (metric: TimelineMetric, value: unknown) => {
    if (typeof value === 'number' && Number.isFinite(value)) points.push({ metric, value });
  };
  switch (source) {
    case 'openpagerank':
      if (facts['found'] === false) break;
      add('page_rank', facts['pageRank']);
      add('referring_domains', facts['referringDomains']);
      break;
    case 'dataforseo':
      add('referring_domains', facts['referringDomains']);
      add('backlinks', facts['backlinks']);
      break;
    case 'ahrefs':
      // Referring domains and traffic come in with the monthly history, dated by Ahrefs.
      add('domain_rating', facts['domainRating']);
      break;
    case 'wayback':
      break;
  }
  return points;
}

/** How long Ahrefs's monthly history is good for before the next month is fetched. */
export const AHREFS_HISTORY_EVERY_MS = 30 * 24 * 60 * 60_000;

/** How far back the first history goes: five years, month by month. */
export const AHREFS_HISTORY_YEARS = 5;

/** Whether Ahrefs's monthly history is due: never taken, or a month old. */
export function ahrefsHistoryDue(historyAt: string | null, now: Date): boolean {
  if (!historyAt) return true;
  return now.getTime() - new Date(historyAt).getTime() >= AHREFS_HISTORY_EVERY_MS;
}

/**
 * Where the next history request starts: five years back the first time, then
 * the month before the last one taken, so a month read half-way is read again whole.
 */
export function ahrefsHistoryFrom(historyAt: string | null, now: Date): string {
  const from = historyAt ? new Date(historyAt) : now;
  const year = from.getUTCFullYear() - (historyAt ? 0 : AHREFS_HISTORY_YEARS);
  const month = from.getUTCMonth() - (historyAt ? 1 : 0);
  return new Date(Date.UTC(year, month, 1)).toISOString().slice(0, 10);
}
